import React from 'react';
import styled from 'styled-components';
import Section from './Section';
import Container from './Container';
// Replacing the react-icons import with Unicode emoji for now
// Later we can reinstall react-icons properly

interface ServiceItemProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  description: string;
}

const ServicesWrapper = styled(Section)`
  background-color: ${props => props.theme.colors.background};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
`;

const Title = styled.h2`
  margin-bottom: ${props => props.theme.spacing.md};
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -${props => props.theme.spacing.sm};
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: ${props => props.theme.colors.primary};
  }
`;

const Introduction = styled.p`
  max-width: 800px;
  margin: 0 auto;
  font-size: ${props => props.theme.fontSizes.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    font-size: ${props => props.theme.fontSizes.md};
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.lg};
  
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.md};
  box-shadow: ${props => props.theme.shadows.md};
  padding: ${props => props.theme.spacing.xl};
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const IconContainer = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.md};
  display: flex;
  justify-content: center;
`;

const ServiceTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  margin-bottom: ${props => props.theme.spacing.xs};
  text-align: center;
`;

const ServiceSubtitle = styled.h4`
  font-size: ${props => props.theme.fontSizes.md};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.accent};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const ServiceDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.md};
  text-align: center;
`;

const ServiceItem: React.FC<ServiceItemProps> = ({ icon, title, subtitle, description }) => {
  return (
    <ServiceCard>
      <IconContainer>{icon}</IconContainer>
      <ServiceTitle>{title}</ServiceTitle>
      <ServiceSubtitle>{subtitle}</ServiceSubtitle>
      <ServiceDescription>{description}</ServiceDescription>
    </ServiceCard>
  );
};

const Services: React.FC = () => {
  const services = [
    {
      icon: '💼',
      title: 'Career Advice',
      subtitle: 'Helping the Community',
      description: 'Guiding individuals to achieve their career goals with tailored advice and support.'
    },
    {
      icon: '📄',
      title: 'CV/Resume Help',
      subtitle: 'One Step at a Time',
      description: 'Assisting with resume building and interview preparation to boost employability.'
    },
    {
      icon: '🏠',
      title: 'Rental Assistance Information',
      subtitle: 'Informing the Community',
      description: 'Providing resources and information to help with housing stability.'
    },
    {
      icon: '🎓',
      title: 'Learning New Skills',
      subtitle: 'One Step at a Time',
      description: 'Offering access to skill-building programs that enhance career and personal growth.'
    },
    {
      icon: '🛒',
      title: 'Grocery Assistance',
      subtitle: 'Reinforcing Our Commitment',
      description: 'Helping families put food on the table with support and resources.'
    },
    {
      icon: '🏥',
      title: 'Paying Medical Bills',
      subtitle: 'Resourcing and Information',
      description: 'Helping families with healthcare costs and finding medical resources.'
    },
    {
      icon: '🏪',
      title: 'Supporting Small Business Set-Up',
      subtitle: 'Helping Self-Sustain',
      description: 'Assisting with foundational steps for starting small businesses for community self-sufficiency.'
    },
    {
      icon: '📚',
      title: 'Continuing Education',
      subtitle: 'Support and Sustain',
      description: 'Providing guidance and resources for individuals pursuing further education.'
    }
  ];

  return (
    <ServicesWrapper id="services">
      <Container>
        <SectionHeader>
          <Title>Ways We Help</Title>
          <Introduction>
            Project Assist is dedicated to changing and improving lives through a holistic approach to
            community challenges. We empower our partners by creating opportunities that benefit
            individuals and entire communities.
          </Introduction>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceItem
              key={index}
              icon={service.icon}
              title={service.title}
              subtitle={service.subtitle}
              description={service.description}
            />
          ))}
        </ServicesGrid>
      </Container>
    </ServicesWrapper>
  );
};

export default Services; 